/**
 * Per-attraction CDN cache-buster stored in sessionStorage.
 *
 * Problem: svc-experiences serves public attraction GETs through a CloudFront
 * layer with `Cache-Control: public, max-age=...`. A PATCH from the admin
 * does not invalidate CDN edges, so a hard refresh within the TTL window
 * pulls the pre-mutation body and the UI looks reverted.
 *
 * Solution: after a successful mutation, store the `updatedAt` returned by
 * the backend as a cache-buster for this attractionId. Subsequent GETs
 * append `?v={updatedAt}` to the URL; CloudFront keys cache entries on the
 * full URL (including query string), so the request misses the cache and
 * hits origin, returning the fresh body.
 *
 * sessionStorage scope is per-tab, which matches admin usage. If another
 * admin mutates the same attraction, this tab still uses its own last-known
 * updatedAt — the first GET with that value is a CDN miss, so origin still
 * serves the most recent DB state.
 */

const KEY_PREFIX = 'attraction-cache-buster:';

function isSessionStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    return false;
  }
}

export function getCacheBuster(attractionId: string): string | undefined {
  if (!isSessionStorageAvailable()) return undefined;
  return window.sessionStorage.getItem(`${KEY_PREFIX}${attractionId}`) ?? undefined;
}

export function setCacheBuster(attractionId: string, updatedAt: string): void {
  if (!isSessionStorageAvailable()) return;
  window.sessionStorage.setItem(`${KEY_PREFIX}${attractionId}`, updatedAt);
}
